import * as Sentry from "@sentry/react";
import React, { useCallback, useEffect, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { useLazyQuery, useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { SUBMIT_APPLICATION } from "api/mutations/application";
import { getPricingExpiredError } from "./utils";
import { APPLICATION_ERROR_CODES } from "utils/consts";
import { QUERY_APPLICATION } from "api/queries/application";
import PageContent from "components/Page/Content";
import Sidebar from "components/Page/Sidebar";
import usePageTitle from "hooks/usePageTitle";
import { APP_URLS } from "settings";
import useApplicationStore from "store/Application";
import { useUserStore } from "store/User";
import { APPLICATION_IN_PROGRESS_KEY } from "utils/consts";
import ApplicationSidebar from "./components/ApplicationSidebar";
import FormErrors from "./components/FormErrors";
import FormNav from "./components/FormNav";
import GetApplicationProgress from "./components/GetApplicationProgress";
import SubmitApplicationProgress from "./components/SubmitApplicationProgress";
import { DEFAULT_FORM_DATA } from "./defaultFormData";
import { FormSchema } from "./formSchema";
import { DEFAULT_STEP, getStepByID, getStepByKey } from "./formSteps";
import { ApiDataType } from "./types";
import { mutationHooks } from "./useFormMutations";
import { validationSchema } from "./validation";

const MultiStepForm: React.FC = () => {
  const [submitError, setSubmitError] = useState(false);
  const [loadingStepChange, setLoadingStepChange] = useState(false);

  const { applicationId: urlApplicationId, step } = useParams<{
    applicationId: string;
    step: string;
  }>();
  const navigate = useNavigate();
  const currentStep = getStepByKey(step || DEFAULT_STEP.key);
  usePageTitle(`${currentStep.name} - Application form`);

  const [
    getApplication,
    {
      data: backendApplication,
      loading: getApplicationLoading,
      error: getApplicationError,
    },
  ] = useLazyQuery(QUERY_APPLICATION, { fetchPolicy: "no-cache" });

  const [submitApplication, submitState] = useMutation(SUBMIT_APPLICATION, {
    onCompleted: (responseData) => {
      const payload = responseData?.applications?.response;
      if (payload?.ok) return handleSubmitSuccess(payload?.application?.uuid);
      const pricingError = getPricingExpiredError(payload);
      if (pricingError) {
        submitState.reset();
        return handlePricingExpired(pricingError.message);
      }
      submitState.reset();
      setSubmitError(true);
    },
    onError: () => {
      submitState.reset();
      setSubmitError(true);
    },
  });

  const { user } = useUserStore();

  const {
    formData,
    setFormData,
    applicationId,
    applicationCase,
    brokerDetailPopulated,
    setApplicationId,
    setApplicationDetail,
    setFormDataFromExistingApplication,
    setAppllicationCase,
    setFormStatus,
    setBrokerDetailPopulated,
    resetApplicantsFormStatus,
    resetApplication,
    setApplicationErrors,
  } = useApplicationStore();

  const {
    control,
    clearErrors,
    setValue,
    watch,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    trigger,
  } = useForm<FormSchema>({
    defaultValues: DEFAULT_FORM_DATA,
    resolver: yupResolver(validationSchema),
  });

  const {
    fields: applicantsFieldArray,
    remove,
    append,
  } = useFieldArray({
    control,
    name: "applicants",
  });

  const numberOfApplicants = useWatch({
    name: "numberOfApplicants",
    control: control,
  });

  const [updateFormStep] = mutationHooks[currentStep.key]();

  useEffect(() => {
    if (urlApplicationId && !applicationId) {
      getApplication({
        variables: {
          uuid: urlApplicationId,
          status: APPLICATION_IN_PROGRESS_KEY,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlApplicationId, applicationId, getApplication]);

  // Auto populate broker details
  useEffect(() => {
    // Only do this if a first time application
    if (applicationId || urlApplicationId) {
      return;
    }
    if (user && !brokerDetailPopulated) {
      setBrokerDetailPopulated();
      setTimeout(() => {
        reset({
          ...DEFAULT_FORM_DATA,
          ...{
            broker: {
              name: user?.fullName,
              firmName: user?.firmName,
              email: user?.email,
              phoneNumber: user?.firmPhoneNumber,
            },
          },
        });
      }, 300);
    }
  }, [
    user,
    applicationId,
    urlApplicationId,
    brokerDetailPopulated,
    setBrokerDetailPopulated,
    reset,
  ]);

  useEffect(() => {
    if (!backendApplication) {
      return;
    }
    setFormDataFromExistingApplication(backendApplication.data.application);
  }, [backendApplication, setFormDataFromExistingApplication]);

  useEffect(() => {
    reset(formData);
    setFormStatus(formData);
  }, [formData, reset, setFormStatus]);

  const handleSubmitSuccess = useCallback(
    (appUuid?: string) => {
      const submittedApplicationUuid = appUuid || urlApplicationId;
      resetApplication();
      reset(DEFAULT_FORM_DATA);
      navigate(
        `${APP_URLS.APPLICATION_FORM}/${submittedApplicationUuid}/complete`,
      );
    },
    [navigate, reset, resetApplication, urlApplicationId],
  );

  const handlePricingExpired = useCallback(
    (message: string) => {
      setApplicationErrors([
        { code: APPLICATION_ERROR_CODES.PRODUCT_PRICING_EXPIRED, message },
      ]);
      setFormData({}, "product");
      navigate(
        `${APP_URLS.APPLICATION_FORM}/${
          applicationId || urlApplicationId
        }/product`,
      );
    },
    [
      applicationId,
      navigate,
      setApplicationErrors,
      setFormData,
      urlApplicationId,
    ],
  );

  // Manage applicants field array count
  useEffect(() => {
    const currentApplicants = applicantsFieldArray.length;
    const targetApplicants = Number(numberOfApplicants);

    if (currentApplicants > 2) {
      return;
    }

    if (targetApplicants > currentApplicants) {
      for (let i = currentApplicants; i < targetApplicants; i++) {
        append(DEFAULT_FORM_DATA.applicants[0]);
      }
      handleNumberOfApplicantsChange();
    } else if (targetApplicants < currentApplicants) {
      for (let i = currentApplicants; i > targetApplicants; i--) {
        remove(i - 1);
      }
      handleNumberOfApplicantsChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberOfApplicants, applicantsFieldArray.length, append, remove]);

  const inSubmitState =
    submitState.data || submitState.loading || submitState.error;

  // Force refresh page to get proper application state with updated number of
  // applicants from BE, instead of programatically trying to sync in client store
  const handleNumberOfApplicantsChange = async () => {
    await handleSaveProgress(false);
    resetApplicantsFormStatus();
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const triggerApiFormUpdate = async (
    data: any,
    validated: boolean = false,
  ) => {
    const apiData: ApiDataType = {
      applicationId: applicationId || urlApplicationId,
      data: currentStep.parser(data, numberOfApplicants, applicantsFieldArray),
      case: applicationCase?.uuid,
      validated: validated,
    };

    if (currentStep.key === "applicants") {
      apiData.numberOfApplicants = numberOfApplicants
        ? Number(numberOfApplicants)
        : 1;
    }

    const { data: response } = await updateFormStep({
      variables: apiData,
    });
    return response;
  };

  const onSubmit = async (data: FormSchema) => {
    if (submitError) {
      setSubmitError(false);
    }

    window.scrollTo(0, 0);

    submitApplication({
      variables: {
        applicationId: applicationId,
      },
    });
  };

  const handleSaveAndNavigate = async (
    navigateStep?: string,
    validate: boolean = false,
  ) => {
    setLoadingStepChange(true);

    if (submitError) {
      setSubmitError(false);
    }

    let isValid = false;

    if (validate) {
      isValid = await trigger(currentStep.key);

      if (!isValid) {
        setLoadingStepChange(false);
        window.scrollTo(0, 0);
        return;
      }
      // Drop stale nested errors (e.g. yup array .test()) that can linger after values are fixed.
      clearErrors(currentStep.key);
    }

    const formValues = { ...getValues(currentStep.key) };
    const stepData = { ...formData[currentStep.key], ...formValues };

    try {
      const response = await triggerApiFormUpdate(stepData, isValid);
      const responseApplication = response?.applications?.data?.application;

      setApplicationId(responseApplication?.uuid);
      setApplicationDetail(responseApplication);
      setAppllicationCase(responseApplication?.case);
      setFormData(
        response?.applications?.data,
        currentStep.key,
        responseApplication?.numberOfApplicants,
      );
      setApplicationErrors([]);

      window.scrollTo(0, 0);

      const _appId = responseApplication?.uuid || applicationId;

      if (navigateStep) {
        navigate(`${APP_URLS.APPLICATION_FORM}/${_appId}/${navigateStep}`);
      } else {
        if (!urlApplicationId && _appId) {
          navigate(
            `${APP_URLS.APPLICATION_FORM}/${_appId}/${currentStep.key}`,
            {
              replace: true,
            },
          );
        }
      }
    } catch (error) {
      Sentry.captureException(error);
      setSubmitError(true);
    }

    setLoadingStepChange(false);
  };

  const handleSaveProgress = async (validate: boolean) => {
    await handleSaveAndNavigate(undefined, validate);
  };

  const handlePrevious = async (validate: boolean) => {
    await handleSaveAndNavigate(getStepByID(currentStep.id - 1).key, validate);
  };

  const handleNext = async () => {
    await handleSaveAndNavigate(getStepByID(currentStep.id + 1).key, true);
  };

  const handleSidebarClick = async (
    stepNumber: number,
    stepKey: string,
    validate: boolean,
  ) => {
    if (stepNumber === currentStep.id) {
      return;
    }
    await handleSaveAndNavigate(stepKey, validate);
  };

  if (!applicationId && (getApplicationLoading || getApplicationError)) {
    return (
      <GetApplicationProgress getApplicationLoading={getApplicationLoading} />
    );
  }

  return (
    <PageContent title="Application Form">
      <div className="flex w-full flex-col md:flex-row md:space-x-8">
        {inSubmitState ? (
          <div className="mx-auto w-full max-w-3xl rounded-lg border border-gray-200 bg-white p-6 text-center md:p-8">
            <SubmitApplicationProgress
              submitApplicationError={submitState.error}
            />
          </div>
        ) : (
          <>
            <Sidebar title="New Application">
              <ApplicationSidebar
                onStepClick={handleSidebarClick}
                currentStep={currentStep}
              />
            </Sidebar>

            <div className="md:basis-3/4">
              <div className="rounded-lg border border-gray-200 bg-white p-6 md:p-8">
                {submitError && (
                  <FormErrors
                    formErrors={{
                      "Save error": {
                        message:
                          "We're unable to save your Application right now, please try again or contact us to discuss",
                      },
                    }}
                  />
                )}

                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className="flex flex-col space-y-2"
                  autoComplete="off"
                  noValidate
                >
                  {currentStep.component && (
                    <currentStep.component
                      control={control}
                      clearErrors={clearErrors}
                      setValue={setValue}
                      watch={watch}
                      register={register}
                      errors={errors}
                      applicantsFieldArray={applicantsFieldArray}
                      getValues={getValues}
                    />
                  )}

                  <FormNav
                    currentStep={currentStep}
                    handlePrevious={handlePrevious}
                    handleSaveProgress={handleSaveProgress}
                    handleNext={handleNext}
                    loadingStepChange={loadingStepChange}
                  />
                </form>
              </div>
            </div>
          </>
        )}
      </div>
    </PageContent>
  );
};

export default MultiStepForm;
